import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import en from "./locales/en";
import es from "./locales/es";
import fr from "./locales/fr";
import de from "./locales/de";
import pt from "./locales/pt";
import hi from "./locales/hi";
import ar from "./locales/ar";

export const LANGUAGES = [
  { code: "en", label: "English", dict: en },
  { code: "es", label: "Español", dict: es },
  { code: "fr", label: "Français", dict: fr },
  { code: "de", label: "Deutsch", dict: de },
  { code: "pt", label: "Português", dict: pt },
  { code: "hi", label: "हिन्दी", dict: hi },
  { code: "ar", label: "العربية", dict: ar, rtl: true },
];

const STORAGE_KEY = "lang";
const byCode = Object.fromEntries(LANGUAGES.map((l) => [l.code, l]));

const detect = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && byCode[saved]) return saved;
  } catch {
    /* storage unavailable */
  }
  const nav = (typeof navigator !== "undefined" && navigator.language ? navigator.language : "en").slice(0, 2);
  return byCode[nav] ? nav : "en";
};

const lookup = (dict, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), dict);

const interpolate = (value, vars) =>
  typeof value === "string" && vars
    ? value.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m))
    : value;

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(detect);
  const meta = byCode[lang];

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = meta.rtl ? "rtl" : "ltr";
  }, [lang, meta]);

  const setLang = useCallback((code) => {
    if (!byCode[code]) return;
    setLangState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* storage unavailable: language still applies for this visit */
    }
  }, []);

  // t("home.hero.title") returns strings, arrays or objects; missing keys fall back to English.
  const t = useCallback(
    (path, vars) => {
      const value = lookup(meta.dict, path);
      return interpolate(value !== undefined ? value : lookup(en, path) ?? path, vars);
    },
    [meta]
  );

  const value = useMemo(() => ({ lang, setLang, t, rtl: Boolean(meta.rtl) }), [lang, setLang, t, meta]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
